import { kaiPartialIdPresenter } from '@kaiizen/kai-presenters';
import {
  getTaskModifierDisplayNames,
  ScopeReportChangeType,
  ScopeReportLineItem,
  TaskModifierDisplayNames,
} from '@kaiizen/kai-pro-api';
import {
  getEffectiveScanValue,
  getEffectiveTaskTrade,
  getLatestProgressStatus,
  isOtherTask,
  ScanGroupOptimized,
  ScanLineItemOptimized,
  ScanOptionOptimized,
  ScanRootOptimized,
  ScanTaskOptimized,
  WorkStatus,
} from '@kaiizen/kai-scan-template-system';
import { GroupedReportLineItems, MaterialOrLaborRecordType, ReportType } from './ScopeReportTypes';

// ***********************************************************************************
// Format Utilities
// ***********************************************************************************

export const formatValue = (value: any) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toLocaleString();
  return value;
};

export const formatCurrency = (value?: number) => {
  if (value === null || value === undefined) return '-';
  return Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value));
};

export const formatCurrencyValue = (value?: number) => {
  if (value === null || value === undefined) return '-';
  return Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false }).format(Number(value));
};

export const formatDate = (date: Date | string) => {
  if (!date) return undefined;
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

/** Calendar dates (e.g. dueDate) are stored as UTC instants; format with UTC components so preview and PDF agree. */
export const formatCalendarDate = (date: Date | string) => {
  if (!date) return undefined;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return undefined;
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const year = d.getUTCFullYear();
  return `${month}/${day}/${year}`;
};

export const formatDateTime = (date: Date | string) => {
  if (!date) return undefined;
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatDecimal = (value?: number, decimalPlaces: number = 2) => {
  if (value === null || value === undefined) return '-';
  return Number(value).toFixed(decimalPlaces);
};

/**
 * Format work status for display
 *
 * @param status - The work status value
 * @returns Formatted work status string
 */
export const formatWorkStatus = (status: WorkStatus | null | undefined): string => {
  if (!status) return '-';
  switch (status) {
    case 'notStarted':
      return 'Not Started';
    case 'inProgress':
      return 'In Progress';
    case 'needsRework':
      return 'Rework';
    case 'completed':
      return 'Completed';
    default:
      return '-';
  }
};

/**
 * Truncate a string to a maximum length, adding ellipsis if truncated
 *
 * @param text - The text to truncate
 * @param maxLength - Maximum length of the resulting string
 * @returns Truncated string with "..." appended if it exceeds maxLength
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

/**
 * Combine an array of note strings and truncate to a maximum length
 *
 * @param notes - Array of note strings to combine
 * @param maxLength - Maximum length of the resulting string (default: 200)
 * @returns Combined and truncated string with "..." appended if it exceeds maxLength
 */
const TRUNCATE_NOTES_MAX_LENGTH = 300;
export const truncateNotesText = (notes: string[], maxLength: number = TRUNCATE_NOTES_MAX_LENGTH): string => {
  const combined = notes.join(' / ');
  if (combined.length <= maxLength) return combined;
  return combined.substring(0, maxLength) + '...';
};

/**
 * Format an array of trade names for display
 *
 * Sorts trades alphabetically and formats each trade name to title case
 * (capitalizing the first letter of each word), then joins them with commas.
 * Special case: "general" is displayed as "General Contractor".
 *
 * @param trades - Array of trade name strings (e.g., ['plumbing', 'electrical', 'general'])
 * @returns Formatted string (e.g., "Electrical, General Contractor, Plumbing")
 */
export const formatTradesForDisplay = (trades: string[]): string => {
  return trades
    .slice() // Create a copy to avoid mutating the original array
    .sort()
    .map(trade => {
      // Special case: "general" becomes "General Contractor"
      if (trade.toLowerCase() === 'general') {
        return 'General Contractor';
      }
      // Capitalize first letter of each word
      return trade
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    })
    .join(', ');
};

// ***********************************************************************************
// Helper Functions
// ***********************************************************************************

/**
 * Get display name for any template-based object with fallback to custom label and default
 *
 * @param template - The template object with name property
 * @param customLabel - The custom label if template name is not available
 * @param defaultName - The default name to use if neither template name nor custom label exists
 * @returns The display name for the object
 */
const getDisplayName = (template: { name?: string } | undefined, customLabel: string | undefined, defaultName: string): string => {
  return (customLabel ? `${template?.name} (${customLabel})` : template?.name) || defaultName;
};

/**
 * Get group display name
 *
 * @param group - The group object
 * @returns The display name for the group
 */
export const getGroupName = (group: ScanGroupOptimized): string => getDisplayName(group.template, group.customLabel, 'Unknown Group');

/**
 * Get task display name
 *
 * For non-standard tasks (`isOtherTask`), uses customLabel, or the template's
 * own name when the task has no label yet.
 *
 * @param task - The task object
 * @returns The display name for the task
 */
export const getTaskName = (task: ScanTaskOptimized): string => {
  if (isOtherTask(task)) {
    return task.customLabel ? `Non-Standard Task: ${task.customLabel}` : getDisplayName(task.template, undefined, 'Unknown Task');
  }
  return getDisplayName(task.template, task.customLabel, 'Unknown Task');
};

/**
 * Get task description with fallback logic for non-standard tasks
 *
 * For non-standard tasks (`isOtherTask`), falls back to
 * task.values.taskDescription if available.
 *
 * @param task - The task object
 * @returns The task description string, or empty string if none available
 */
export const getTaskDescription = (task: ScanTaskOptimized): string => {
  if (isOtherTask(task)) {
    // we do not return the task description for non-standard tasks
    // because getScopePDFTaskDescName already handles this for us
    return '';
  }
  return task.template?.description || '';
};

/**
 * Get option display name
 *
 * @param option - The option object
 * @returns The display name for the option
 */
export const getOptionName = (option: ScanOptionOptimized): string => getDisplayName(option.template, option.customLabel, 'Unknown Option');

/**
 * Get line item display name
 *
 * @param lineItem - The line item object
 * @returns The display name for the line item
 */
export const getLineItemName = (lineItem: ScanLineItemOptimized): string => getDisplayName(lineItem.template, lineItem.customLabel, 'Unknown Line Item');

/**
 * Get scope PDF task description name
 *
 * For non-standard tasks, always uses option name.
 * For standard tasks with SKU details, uses option name.
 * Otherwise, uses line item name.
 *
 * @param task - The task object
 * @param option - The option object
 * @param lineItem - The line item object
 * @returns The name to use for the scope PDF task description
 */
export const getScopePDFTaskDescName = (task: ScanTaskOptimized, option: ScanOptionOptimized, lineItem: ScanLineItemOptimized): string => {
  if (isOtherTask(task)) {
    return String(task.values.taskDescription?.setValue || '');
  }
  return lineItem.skuDetails?.title ? getOptionName(option) : getLineItemName(lineItem);
};

/**
 * Get GL code from task template customer metadata
 *
 * @param task - The task object
 * @returns The GL code as a string, or undefined if not present
 */
export const getGlCode = (task: ScanTaskOptimized): string | undefined =>
  task.template?.customerMetadata?.GLCode ? String(task.template.customerMetadata.GLCode) : undefined;

/**
 * Get the work status for a task if it's a valid status value
 *
 * @param task - The task to get work status for
 * @returns The work status, or null if no valid status has been set
 */
export const getTaskWorkStatus = (task: ScanTaskOptimized): WorkStatus | undefined => getLatestProgressStatus(task) || 'notStarted';

/**
 * Extract numeric value from a scan value object
 *
 * @param value - The scan value object with calculatedValue and setValue
 * @returns The numeric value or undefined if not a number
 */
export const getNumericValue = (value: any): number | undefined => {
  if (typeof value?.setValue === 'number') return value.setValue;
  if (typeof value?.calculatedValue === 'number') return value.calculatedValue;
  return undefined;
};

/**
 * Parse a value from JSON customerMetadata as a number
 * Handles cases where JSON fields may be stored as strings
 *
 * @param value - The value to parse (may be number, string, null, or undefined)
 * @returns The numeric value or undefined if null/undefined
 */
export const parseMetadataNumber = (value: unknown): number | undefined => value != null ? Number(value) : undefined;

/**
 * Extract line item quantity from a line item object
 *
 * @param lineItem - The line item object with values
 * @param defaultValue - The default value to use if not set
 * @returns The line item quantity
 */
export const getLineItemQuantity = (lineItem: ScanLineItemOptimized): number => {
  // lineItems can have a quantity key that is undefined, null, 0
  // for reporting we want to filter out lineItems with quantity of 0
  // so we avoid defaulting here
  const quantity = getEffectiveScanValue(lineItem.values?.quantity);
  return quantity != null ? Number(quantity) : 1;
};

/**
 * Extract cost information from a line item
 *
 * @param lineItem - The line item to extract costs from
 * @returns Object containing material cost, labor cost, unit price, and total cost
 */
export const extractLineItemCosts = (lineItem: ScanLineItemOptimized, recordType: 'materials' | 'labor'): { totalLineItemCost: number; unitPrice: number } => {
  const materialCost = lineItem.optimized?.reducedValues?.materialCost || undefined;
  const laborCost = lineItem.optimized?.reducedValues?.laborCost || undefined;
  let unitPrice = getNumericValue(lineItem.values?.price) || 0;
  let unitLaborPrice = getNumericValue(lineItem.values?.laborPrice) || 0;

  return {
    totalLineItemCost: (materialCost || 0) + (laborCost || 0),
    unitPrice: recordType === 'materials' ? unitPrice + unitLaborPrice : unitLaborPrice,
  };
};

/**
 * Get contractor ID for a task with fallback to default contractors
 *
 * @param task - The task object
 * @param optimizedScan - The optimized scan data containing default contractors
 * @returns The contractor ID for the task
 */
export const getContractorIdForTask = (task: ScanTaskOptimized, optimizedScan: ScanRootOptimized): string => {
  const trade = getEffectiveTaskTrade(task);
  return task.assignedContractorId
    || optimizedScan.defaultContractorsByTrade?.[trade]
    || optimizedScan.defaultContractorsByTrade?.['general']
    || 'unassigned';
};

/**
 * Get trade for a task
 *
 * @param task - The task object
 * @returns The trade for the task
 */
export const getTradeForTask = (task: ScanTaskOptimized): string => {
  return getEffectiveTaskTrade(task);
};

/**
 * Find the status value key for a task by looking for a template value with valueOptions
 * that has a corresponding setValue in the task values
 *
 * @param task - The task object to search
 * @returns The key name of the status value, or undefined if not found
 */
export const findStatusValueKey = (task: ScanTaskOptimized): string | undefined => {
  const templateValues = task.template?.values;
  if (!templateValues) return undefined;

  for (const key of Object.keys(templateValues)) {
    const templateValue = templateValues[key];
    if (templateValue?.valueOptions && task.values?.[key]?.setValue) {
      return key;
    }
  }
  return undefined;
};

/**
 * Determine if a task is a status task
 *
 * A status task is one where the task template has at least one value with valueOptions
 * AND a corresponding setValue in task values.
 *
 * Note: A status task may still have options with line items. The caller should check
 * for options separately if needed to determine how to render the task.
 *
 * @param task - The task object to check
 * @returns true if this is a status task, false otherwise
 */
export const isStatusTask = (task: ScanTaskOptimized): boolean => !!findStatusValueKey(task);

/**
 * Check if a task has any options with line items
 *
 * @param task - The task object to check
 * @returns true if the task has at least one option with at least one line item
 */
export const taskHasOptionsWithLineItems = (task: ScanTaskOptimized): boolean => (task.options ?? []).some(option => (option.lineItems ?? []).length > 0);

/**
 * Get the status label for a status task by looking up the selected value in the template's value options
 *
 * @param task - The status task object
 * @returns The human-readable status label, or the raw value if no matching option is found
 */
export const getStatusTaskLabel = (task: ScanTaskOptimized): string => {
  const statusKey = findStatusValueKey(task);
  if (!statusKey) return '';

  const templateValue = task.template?.values?.[statusKey];
  const selectedValue = String(task.values[statusKey]?.setValue ?? '');
  const selectedOption = templateValue?.valueOptions?.find(
    (option: any) => String(option.value) === selectedValue,
  );
  return selectedOption?.label || selectedValue;
};

/**
 * Get the status value for a status task
 *
 * @param task - The status task object
 * @returns The raw status value, or empty string if not found
 */
export const getStatusTaskValue = (task: ScanTaskOptimized): string => {
  const statusKey = findStatusValueKey(task);
  if (!statusKey) return '';
  return String(task.values[statusKey]?.setValue ?? '');
};

/**
 * Find the first contractor assigned to a general trade task
 *
 * Scans through all tasks in the optimized scan and returns the first contractor
 * that is assigned to a task with trade 'general'.
 *
 * @param optimizedScan - The optimized scan data containing groups and tasks
 * @returns The contractor ID of the first general contractor found, or undefined if none found
 */
export const getFirstAssignedGeneralContractor = (optimizedScan: ScanRootOptimized): string | undefined => {
  for (const group of optimizedScan.groups ?? []) {
    for (const task of group.tasks ?? []) {
      const trade = getEffectiveTaskTrade(task);
      if (trade === 'general' && task.assignedContractorId) {
        return task.assignedContractorId;
      }
    }
  }
  return undefined;
};

// ***********************************************************************************
// Data Transformation Utilities
// ***********************************************************************************

/**
 * Identify if a line item is about materials or labor
 *
 * @param lineItem - The line item object to analyze
 * @returns MaterialOrLaborRecordType based on line item name and values
 */
export const categorizeLineItem = (lineItem: ScanLineItemOptimized): MaterialOrLaborRecordType => {
  if (lineItem.values) {
    const keys = Object.keys(lineItem.values);
    const hasLaborCost = keys.some(key => key.toLowerCase() === 'laborcost');
    const hasMaterialCost = keys.some(key => key.toLowerCase() === 'materialcost');

    if (hasLaborCost && !hasMaterialCost) {
      return 'labor';
    }

    if (hasMaterialCost && !hasLaborCost) {
      return 'materials';
    }

    if (hasMaterialCost && hasLaborCost) {
      return 'materials';
    }
  }
  throw new Error('Unable to categorize line item: must have either laborcost key or both materialcost and laborcost keys');
};

/**
 * Find line item key for comparison
 *
 * @param item - The SOW data item
 * @returns Composite key string for line item identification
 */
export const findLineItemKey = (item: ScopeReportLineItem): string => {
  return `${item.groupId}-${item.taskId}-${item.optionId}-${item.lineItemId}`;
};

/**
 * Calculate total cost for a group from grouped SOW data
 *
 * @deprecated This function is deprecated use calculateGroupCostByGroupId instead
 * @param groupSowData - Grouped SOW data
 * @param groupName - Name of the group to calculate cost for
 * @returns Total cost for the group
 */
export const calculateGroupCost = (groupSowData: GroupedReportLineItems, groupName: string): number => {
  const items = groupSowData[groupName] || [];
  return items.reduce((sum, item) => sum + (item.totalLineItemCost || 0), 0);
};

/**
 * Calculate total cost for a group from grouped SOW data
 *
 * @param groupSowData - Grouped SOW data
 * @param groupName - Name of the group to calculate cost for
 * @returns Total cost for the group
 */
export const calculateGroupCostByGroupId = (groupSowData: GroupedReportLineItems, groupId: string): number => {
  const items = groupSowData[groupId] || [];
  return items.reduce((sum, item) => sum + (item.totalLineItemCost || 0), 0);
};

// ***********************************************************************************
// Report Display Utilities
// ***********************************************************************************

/**
 * Get status color for scan lifecycle status
 * Used for status badges and deferred items borders
 *
 * @param scanLifecycleStatus - The scan lifecycle status
 * @returns Hex color code for the status
 */
export const getStatusColor = (scanLifecycleStatus?: string, reportType?: ReportType): string => {
  if (!scanLifecycleStatus) return '#565656';

  const normalizedStatus = scanLifecycleStatus.toLowerCase().replace('_', '');

  if (normalizedStatus === 'approved') {
    return '#4caf50'; // Green for Approved
  }

  if (normalizedStatus === 'inreview' || normalizedStatus === 'inprogress') {
    switch (reportType) {
      case 'punchlist':
        return '#40879F';
      case 'changeOrder':
        return '#CBB964';
      case 'fullScope':
      default:
        return '#565656';
    }
  }

  return '#565656'; // Grey for everything else
};

export const isTerminalState = (scanLifecycleStatus?: string): boolean => {
  return scanLifecycleStatus === 'approved' || scanLifecycleStatus === 'rejected';
};

/**
 * Get human-readable label for scan lifecycle status
 *
 * @param scanLifecycleStatus - The scan lifecycle status code
 * @returns Formatted status label for display
 */
export const getStatusLabel = (scanLifecycleStatus?: string): string => {
  if (!scanLifecycleStatus) return '';

  switch (scanLifecycleStatus) {
    case 'created':
    case 'dispatched':
    case 'pendingReview':
    case 'inReview':
      return 'IN REVIEW';
    case 'inProgress':
      return 'IN PROGRESS';
    case 'approved':
      return 'APPROVED';
    case 'rejected':
      return 'REJECTED';
    case 'correctionsRequested':
      return 'CORRECTIONS REQUESTED';
    default:
      return scanLifecycleStatus;
  }
};

/**
 * Get badge color based on scan lifecycle status
 * Used for status badges and deferred items borders
 *
 * @deprecated This function is deprecated use getBadgeColorScopeState instead
 * @param scanLifecycleStatus - The scan lifecycle status
 * @returns Hex color code for the status
 */
export const getBadgeColor = (
  scanLifecycleStatus?: string,
  reportType?: ReportType,
  isPendingScope?: boolean,
  isApprovedScope?: boolean,
  isHistoricalScope?: boolean,
  isPrint?: boolean,
): string => {
  if (isPendingScope) {
    return '#898989'; // darkerGrey from COLORS2
  }
  if (isApprovedScope) {
    return '#7cb88e'; // green from COLORS2
  }
  return '#898989'; // darkerGrey from COLORS2
};

/**
 * Get badge color based on scan lifecycle status
 * Used for status badges and deferred items borders
 *
 * @param scanLifecycleStatus - The scan lifecycle status
 * @returns Hex color code for the status
 */
export const getBadgeColorScopeState = (
  scanLifecycleStatus?: string,
  reportType?: ReportType,
  scanScopeState?: string,
): string => {
  // i guess punchlist are only in draft state? :shrug:
  if (reportType === 'punchlist') {
    return '#898989'; // darkerGrey from COLORS2
  }
  if (scanScopeState === 'draft') {
    return '#898989'; // darkerGrey from COLORS2
  }
  if (scanScopeState === 'current') {
    return '#7cb88e'; // green from COLORS2
  }
  return '#898989'; // darkerGrey from COLORS2
};

/**
 * Get badge title based on scan lifecycle status
 *
 * @deprecated This function is deprecated use getBadgeTitleScopeState instead
 * @param reportType - The type of report (fullScope, changeOrder, punchlist, closeout)
 * @returns Formatted report title for display
 */
export const getBadgeTitle = (
  scanLifecycleStatus?: string,
  reportType?: ReportType,
  isPendingScope?: boolean,
  isApprovedScope?: boolean,
  isHistoricalScope?: boolean,
  isPrint?: boolean,
): string => {
  if (isHistoricalScope) {
    return 'OUTDATED';
  }
  if (isApprovedScope) {
    return 'CURRENT';
  }
  if (isPendingScope) {
    return 'DRAFT';
  }
  return 'UNKNOWN';
};

/**
 * Get badge title based on scan lifecycle status
 *
 * @param reportType - The type of report (fullScope, changeOrder, punchlist, closeout)
 * @returns Formatted report title for display
 */
export const getBadgeTitleScopeState = (
  scanLifecycleStatus?: string,
  reportType?: ReportType,
  scanScopeState?: string,
): string => {
  // i guess punchlist are only in draft state? :shrug:
  if (reportType === 'punchlist') {
    return 'DRAFT';
  }
  if (scanScopeState === 'outdated') {
    return 'OUTDATED';
  }
  if (scanScopeState === 'current') {
    return 'CURRENT';
  }
  if (scanScopeState === 'draft') {
    return 'DRAFT';
  }
  return 'UNKNOWN';
};

/**
 * Get report title based on report type
 *
 * @deprecated This function is deprecated b/c we don't need user feedback titles anymore
 * @param scanLifecycleStatus - The scan lifecycle status
 * @param reportType - The type of report (fullScope, changeOrder, punchlist, closeout)
 * @param isPendingScope - Whether this is a pending scope
 * @param isApprovedScope - Whether this is an approved scope
 * @param isHistoricalScope - Whether this is a historical scope
 * @param isPrint - Whether this is a print version of the report
 * @returns Formatted report title for display
 */
export const getUserFeedbackTitle = (
  scanLifecycleStatus?: string,
  reportType?: ReportType,
  isPendingScope?: boolean,
  isApprovedScope?: boolean,
  isHistoricalScope?: boolean,
  isPrint?: boolean,
): string => {
  if (isPendingScope) {
    return 'LIVE SCOPE';
  } else if (isApprovedScope) {
    return 'APPROVED SCOPE';
  } else if (isHistoricalScope) {
    return 'HISTORIC DOCUMENT';
  } else {
    return 'UNKNOWN';
  }
};

/**
 * Get report description text for user feedback banner
 *
 * @deprecated This function is deprecated use getUserFeedbackDescriptionScopeState instead
 * @param isPendingScope - Whether this is a pending scope
 * @param isApprovedScope - Whether this is an approved scope
 * @param isHistoricalScope - Whether this is a historical scope
 * @param reportType - The type of report (fullScope, changeOrder, punchlist, closeout)
 * @param isPrintVersion - Whether this is a print version of the report
 * @returns Description text for the report
 */
export const getUserFeedbackDescription = (
  scanLifecycleStatus?: string,
  reportType?: ReportType,
  isPendingScope?: boolean,
  isApprovedScope?: boolean,
  isHistoricalScope?: boolean,
  isPrint?: boolean,
  approvedDate?: string,
): string => {
  const title = getReportTitle(reportType)
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  if (isHistoricalScope) {
    const datePhrase = approvedDate ? ` due to approved changes on ${approvedDate}` : '';
    return `This ${title} is outdated${datePhrase}. For historical reference only.`;
  }

  if (isApprovedScope) {
    const datePhrase = approvedDate ? ` as of ${approvedDate}` : '';
    return `This is the approved ${title}${datePhrase}. Always verify you are working from the current version.`;
  }

  if (isPendingScope) {
    return `This ${title} is in progress. Project work can begin once approved.`;
  }

  return '';
};

/**
 * Get report description text for user feedback banner
 *
 * @param scanScopeState - The scan scope state
 * @param reportType - The type of report (fullScope, changeOrder, punchlist, closeout)
 * @param approvedDate - The date the scope was approved
 * @returns Description text for the report
 */
export const getUserFeedbackDescriptionScopeState = (
  scanLifecycleStatus?: string,
  reportType?: ReportType,
  scanScopeState?: string,
  approvedDate?: string,
): string => {
  const title = getReportTitle(reportType)
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  if (reportType === 'punchlist') {
    return 'Rework items will remain in the punch list until they are resolved.';
  }

  if (scanScopeState === 'outdated') {
    const datePhrase = approvedDate ? ` due to approved changes on ${approvedDate}` : '';
    return `This ${title} is outdated${datePhrase}. For historical reference only.`;
  }

  if (scanScopeState === 'current') {
    const datePhrase = approvedDate ? ` as of ${approvedDate}` : '';
    return `This is the approved ${title}${datePhrase}. Always verify you are working from the current version.`;
  }

  if (scanScopeState === 'draft') {
    return `This ${title} is in progress. Project work can begin once approved.`;
  }

  return '';
};

/**
 * Get user feedback banner color based on scan lifecycle status
 * Used for status badges and deferred items borders
 *
 * @deprecated This function is deprecated use getUserFeedbackBannerColorScopeState instead
 * @param scanLifecycleStatus - The scan lifecycle status
 * @param reportType - The type of report (fullScope, changeOrder, punchlist, closeout)
 * @param isPendingScope - Whether this is a pending scope
 * @param isApprovedScope - Whether this is an approved scope
 * @param isHistoricalScope - Whether this is a historical scope
 * @param isPrint - Whether this is a print version of the report
 * @returns Hex color code for the status
 */
export const getUserFeedbackBannerColor = (
  scanLifecycleStatus?: string,
  reportType?: ReportType,
  isPendingScope?: boolean,
  isApprovedScope?: boolean,
  isHistoricalScope?: boolean,
  isPrint?: boolean,
): string => {
  if (reportType === 'changeOrder' && isPendingScope) {
    return '#FEB05D'; // attentionOrange from COLORS2
  }
  if (isHistoricalScope) {
    return '#898989'; // darkerGrey from COLORS2
  }
  if (isApprovedScope) {
    return '#7cb88e'; // darkerGray from COLORS2
  }
  if (isPendingScope) {
    return '#7cb88e'; // green from COLORS2
  }
  return '#898989'; // darkerGrey from COLORS2
};

/**
 * Get user feedback banner color based on scan lifecycle status
 * Used for status badges and deferred items borders
 *
 * @param scanLifecycleStatus - The scan lifecycle status
 * @param reportType - The type of report (fullScope, changeOrder, punchlist, closeout)
 * @param scanScopeState - The scan scope state
 * @returns Hex color code for the status
 */
export const getUserFeedbackBannerColorScopeState = (
  reportType?: ReportType,
  scanScopeState?: string,
): string => {
  if (reportType === 'changeOrder' && scanScopeState === 'draft') {
    return '#FEB05D'; // attentionOrange from COLORS2
  }
  if (reportType === 'punchlist') {
    return '#95D5EB'; // blue from COLORS2
  }
  if (scanScopeState === 'outdated') {
    return '#898989'; // darkerGrey from COLORS2
  }
  if (scanScopeState === 'current') {
    return '#7cb88e'; // green from COLORS2
  }
  if (scanScopeState === 'draft') {
    return '#7cb88e'; // green from COLORS2
  }
  return '#898989'; // darkerGrey from COLORS2
};

/**
 * Get report title based on report type
 *
 * @param reportType - The type of report (fullScope, changeOrder, punchlist, closeout)
 * @returns Formatted report title for display
 */
export const getReportTitle = (reportType?: ReportType, isAddendum: boolean = false): string => {
  switch (reportType) {
    case 'fullScope':
      return isAddendum ? 'SCOPE OF WORK ADDENDUM' : 'SCOPE OF WORK';
    case 'changeOrder':
      return isAddendum ? 'CHANGE ORDER ADDENDUM' : 'CHANGE ORDER';
    case 'punchlist':
      return 'PUNCH LIST';
    case 'closeout':
      return isAddendum ? 'CLOSE OUT ADDENDUM' : 'CLOSE OUT';
    default:
      return isAddendum ? 'SCOPE OF WORK ADDENDUM' : 'SCOPE OF WORK';
  }
};

export const EXPORT_DOC_ID_PREFIX = 'EXP';
export const getDocIdPrefix = (reportType?: ReportType): string => {
  switch (reportType) {
    case 'fullScope':
      return 'SOW';
    case 'changeOrder':
      return 'CO';
    case 'punchlist':
      return 'PL';
    case 'closeout':
      return 'FIN';
    default:
      return 'SOW';
  }
};

export const getDocId = (reportType: ReportType, scanId: string): string => {
  return `${getDocIdPrefix(reportType)}-${kaiPartialIdPresenter(scanId)}`;
};

/**
 * Get document type name for filenames based on report type
 *
 * @param reportType - The type of report (fullScope, changeOrder, punchlist, closeout)
 * @returns Document type name suitable for filenames (e.g., "Scope", "Change Order")
 */
export const getDocumentTypeName = (reportType?: ReportType): string => {
  switch (reportType) {
    case 'fullScope':
      return 'Scope';
    case 'changeOrder':
      return 'Change Order';
    case 'punchlist':
      return 'Punch List';
    case 'closeout':
      return 'Closeout';
    default:
      return 'Report';
  }
};

/**
 * Generate change type label showing which columns changed in a change order
 *
 * @param changeOrderData - The change order data containing changedColumns flags
 * @param modifierLabels - The org's modifier labels; omitted labels fall back to the defaults
 * @returns Formatted label string like "Change: Contractor, Quantity" or empty string if no changes
 */
export const getChangeTypeLabel = (
  changeOrderData: {
    changedColumns: { contractor: boolean; quantity: boolean; rate: boolean; amount: boolean; deferred: boolean; tenantResponsibility: boolean };
    changeType?: ScopeReportChangeType;
  },
  modifierLabels: Partial<TaskModifierDisplayNames> = {},
): string => {
  const { changeType } = changeOrderData;

  if (changeType === 'new') return 'New Item';
  if (changeType === 'deleted') return 'Removed';

  const changedColumns = [];
  if (changeOrderData.changedColumns.contractor) changedColumns.push('Contractor');
  if (changeOrderData.changedColumns.quantity) changedColumns.push('Quantity');
  if (changeOrderData.changedColumns.rate) changedColumns.push('Rate');
  const { deferred, tenantResponsibility } = { ...getTaskModifierDisplayNames(undefined), ...modifierLabels };
  if (changeOrderData.changedColumns.deferred) changedColumns.push(deferred);
  if (changeOrderData.changedColumns.tenantResponsibility) changedColumns.push(tenantResponsibility);

  const changedColumnsText = changedColumns.length > 0 ? `: ${changedColumns.join(', ')}` : '';

  if (changeType === 'reversal') return `Reversal${changedColumnsText}`;
  if (changeType === 'restatement') return `Restatement${changedColumnsText}`;
  if (changeType === 'modified') return `Modified${changedColumnsText}`;

  if (changedColumns.length !== 0) {
    return `Change${changedColumnsText}`;
  }

  return '';
};
